import React from 'react';
import { Card } from 'react-bootstrap';
import {
  AdvancedEstimateSections,
  EstimateSection,
  SECTION_TITLES,
  CUSTOMER_VISIBLE_SECTIONS,
  isTextContent,
  isTimelineContent,
  EstimateSectionType
} from '../types/estimateSectionTypes';

interface AdvancedSectionsDisplayProps {
  advancedSections?: AdvancedEstimateSections;
}

const AdvancedSectionsDisplay: React.FC<AdvancedSectionsDisplayProps> = ({
  advancedSections
}) => {
  // Return null if no sections
  if (!advancedSections || !advancedSections.sections) {
    return null;
  }

  // Filter to only enabled sections that customers can see
  const visibleSections = advancedSections.sectionOrder
    .map(id => advancedSections.sections.find(s => s.id === id))
    .filter((section): section is EstimateSection =>
      section !== undefined &&
      section.enabled &&
      CUSTOMER_VISIBLE_SECTIONS.includes(section.type)
    );

  // Return null if no visible sections
  if (visibleSections.length === 0) {
    return null;
  }

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'To be determined';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Render section content based on type
  const renderSectionContent = (section: EstimateSection) => {
    const { content } = section;

    // Text sections (Scope of Work, Exclusions, Warranty)
    if (isTextContent(content)) {
      return (
        <div className="section-text">
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {content.text || 'No details provided.'}
          </p>
        </div>
      );
    }

    // Timeline section
    if (isTimelineContent(content)) {
      const { data } = content;
      return (
        <div className="timeline-content">
          <div className="row mb-5">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <i className="fas fa-calendar-alt text-primary me-2"></i>
                <div>
                  <small className="text-muted d-block">Start Date</small>
                  <strong>{formatDate(data.startDate)}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <i className="fas fa-flag-checkered text-success me-2"></i>
                <div>
                  <small className="text-muted d-block">Estimated Completion</small>
                  <strong>{formatDate(data.endDate)}</strong>
                </div>
              </div>
            </div>
          </div>

          {data.milestones && data.milestones.length > 0 && (
            <div className="milestones mt-3 mb-5">
              <h6 className="mb-3">
                <i className="fas fa-tasks me-2"></i>
                Project Milestones
              </h6>
              <div className="list-group">
                {data.milestones.map((milestone, index) => (
                  <div key={milestone.id || index} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{milestone.name}</h6>
                        {milestone.description && (
                          <p className="mb-0 text-muted small">{milestone.description}</p>
                        )}
                      </div>
                      {milestone.date && (
                        <span className="badge bg-secondary">
                          {formatDate(milestone.date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Get icon for section type
  const getSectionIcon = (type: EstimateSectionType): string => {
    switch (type) {
      case 'scope_of_work':
        return 'fas fa-clipboard-list';
      case 'timeline':
        return 'fas fa-calendar-alt';
      case 'exclusions':
        return 'fas fa-exclamation-triangle';
      case 'warranty':
        return 'fas fa-shield-alt';
      default:
        return 'fas fa-info-circle';
    }
  };

  return (
    <Card className="shadow mb-4">
      <Card.Header className="bg-primary bg-opacity-10 py-3">
        <h4 className="m-0">
          <i className="fas fa-file-alt me-2"></i>
          Estimate Details
        </h4>
      </Card.Header>
      <Card.Body className="p-4">
        {visibleSections.map((section, index) => (
          <div key={section.id} className={index < visibleSections.length - 1 ? 'mb-4' : ''}>
            <h5 className="mb-3">
              <i className={`${getSectionIcon(section.type)} me-2`}></i>
              {SECTION_TITLES[section.type] || section.type}
            </h5>
            {renderSectionContent(section)}
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

export default AdvancedSectionsDisplay;
